import {
  AppName,
  FieldMapValue,
  GoogleUserProps,
  OnUserSnapshot,
  ProfileType,
  UpdateCallback,
  User,
  UserCallback,
  UserInviteProps,
  UserRole,
  USERS,
  version,
  WithId,
} from '@hb/shared'
import { Auth, sendPasswordResetEmail as firebaseSendPasswordResetEmail } from 'firebase/auth'
import {
  collection,
  doc,
  DocumentSnapshot,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import cloneDeep from 'lodash.clonedeep'
import { USER_INVITES_REF, USER_ROLES_REF, USERS_REF } from '../db/collections'
import { db } from '../db/db'
import { functions } from '../functions/functions'
import { inviteUserFunction } from '../functions/other'

export const nestedDeleteUndefined = (inp: any) => {
  const o = cloneDeep(inp)
  if (typeof o === 'object' && !(o instanceof Array) && o !== null) {
    const keys = Object.keys(o)

    for (const key of keys) {
      const child = o[key]
      if (child === undefined) {
        delete o[key]
      }
      if (typeof child === 'object' && !(child instanceof Array)) {
        o[key] = nestedDeleteUndefined(child)
      }
    }
  }
  return o
}

const getPasswordResetUrl = (appName: AppName) => {
  switch (appName) {
    case 'providers-app':
      return version === 'dev'
        ? 'https://hbportal-dev.web.app'
        : 'https://providers.hamiltonbilling.com'
    default:
      switch (version) {
        case 'dev':
          return 'https://hamilton-billing-dev.web.app'
        case 'staging':
          return 'https://hamiltonbilling-staging.web.app'
        default:
          return 'https://hamiltonbilling.com'
      }
  }
}

export const sendPasswordResetEmail = (
  auth: Auth,
  { email, appName }: { email: string; appName: AppName },
) =>
  firebaseSendPasswordResetEmail(auth, email, {
    url: getPasswordResetUrl(appName),
  })

// updates field in user object - key can be nested
export async function dbUpdateUser(uid: string, key: string, data: any): Promise<UpdateCallback> {
  let updated

  if (typeof data === 'object' && !(data instanceof Array) && data !== null) {
    updated = { ...nestedDeleteUndefined(data), updatedOn: Date.now() }
  } else {
    updated = data
  }
  const userDoc = await getDoc(doc(USERS_REF, uid))
  const isInvite = !userDoc.exists
  try {
    await setDoc(
      doc(isInvite ? USER_INVITES_REF : (USERS_REF as any), uid),
      { [key]: updated },
      { merge: true },
    )
  } catch (err) {
    console.error(err)
    return { error: 'Error updating user' }
  }
  return { success: 'Successfully updated!' }
}
export const populateUserData = (
  authUser: GoogleUserProps,
  dbUser: Omit<User, 'id'>,
): WithId<User> => {
  const { uid, displayName, email } = authUser
  return {
    ...dbUser,
    id: uid,
    name: displayName ?? undefined,
    email: email ?? null,
    // availableForms: populateAvailableForms(dbUser),
  }
}
let creatingUser = false
let hasSignedIn = false
function createUser(
  uid: string,
  userItem: Omit<User, 'id' | 'forceUpdateOn'>,
  callback: (c: UserCallback) => void,
  onUserSnapshot: (s: DocumentSnapshot<User>) => void,
) {
  if (creatingUser) return
  creatingUser = true
  const userRef = doc(USERS_REF, uid)
  return setDoc(userRef, userItem as any, { merge: true })
    .then(() => {
      hasSignedIn = true
      onSnapshot(userRef, s => onUserSnapshot(s))
      callback({
        success: 'Welcome to Hamilton Billing!',
      })
    })
    .catch(err => {
      console.error(err)
      callback({ error: 'Error signing up.' })
    })
    .finally(() => {
      creatingUser = false
    })
}

export function dbGetOrCreateUser(
  p: GoogleUserProps,
  callback: (
    c: UserCallback,
    showSuccess: (message: string) => void,
    showError: (message: string) => void,
  ) => void,
  onUserSnapshot: OnUserSnapshot,
  showSuccess: (message: string) => void,
  showError: (message: string) => void,
) {
  const { uid, displayName, email } = p
  const roleRef = doc(USER_ROLES_REF, uid)
  const handleSnapshot = (s: DocumentSnapshot<User>, role: UserRole) => {
    onUserSnapshot(p, { ...s.data(), role } as Omit<User, 'id'>, showSuccess, showError)
  }

  const onRoleSnapshot = (role: UserRole) => {
    const userRef = doc(USERS_REF, uid)
    getDoc(userRef)
      .then(res => {
        if (res.exists()) {
          handleSnapshot(res, role)
          onSnapshot(userRef, s => handleSnapshot(s, role))
          if (!hasSignedIn) {
            callback(
              {
                success: 'Welcome back!',
              },
              showSuccess,
              showError,
            )
            hasSignedIn = true
          }
        } else {
          const now = Date.now()
          const trimmedEmail = email?.trim() || null
          // const emailLower = trimmedEmail?.toLowerCase() || null
          const userItem: Omit<User, 'forceUpdateOn'> = {
            isInvite: false,
            createdOn: now,
            fname: null,
            lname: null,
            email: trimmedEmail,
            // emailLower,
          }
          if (displayName) userItem.name = displayName
          userItem.joinedOn = Date.now()

          return createUser(
            uid,
            userItem,
            c => callback(c, showSuccess, showError),
            s => handleSnapshot(s, role),
          )
        }
      })
      .catch(err => {
        console.error(err)
        callback({ error: 'Error signing in.' }, showSuccess, showError)
      })
  }

  return onSnapshot(
    roleRef,
    (s: DocumentSnapshot) => {
      const { role } = s.data() ?? {}
      if (role) onRoleSnapshot(role)
      else onRoleSnapshot('user')
    },
    err => {
      console.error(roleRef.path)
      console.error(err)
    },
  )
}

const getEmailVerifiedFunction = httpsCallable<{ userId: string }, boolean>(
  functions,
  'getEmailVerified',
)
const manuallySetEmailVerifiedFunction = httpsCallable(functions, 'manuallySetEmailVerified')

export const getEmailVerified = async (userId: string) => {
  const res = await getEmailVerifiedFunction({ userId })
  return res.data
}
export const manuallySetEmailVerified = async (userId: string) => {
  const res = await manuallySetEmailVerifiedFunction({ userId })
  return res.data
}

export const getPracticeUserIdFromEmail = async (email: string) => {
  if (!email) return null
  const emailLower = email.toLowerCase()
  const user = await getDocs(query(collection(db, USERS), where('emailLower', '==', emailLower)))
  return user.docs[0]?.id
}

export const inviteUser = async (
  appName: AppName,
  data: FieldMapValue,
  profileType: ProfileType,
  practiceId?: string | null,
): Promise<{ patientId: string; pregnancyId: string | null }> => {
  const { email, fname, lname, role, phone, dob, messageToAdmins, verifyEmail } = data
  const submitted = {
    fname,
    lname,
    role,
    profileType,
    verifyEmail: !!verifyEmail,
    createdOn: Date.now(),
  } as Omit<UserInviteProps & { practiceId?: string; messageToAdmins?: string }, 'id' | 'appName'>

  if (email) submitted.email = email
  if (phone) submitted.phone = phone
  if (dob) submitted.dob = dob
  if (messageToAdmins) submitted.messageToAdmins = messageToAdmins

  if (practiceId) {
    submitted.practiceId = practiceId
  }
  try {
    const res = await inviteUserFunction({
      appName,
      ...submitted,
    })
    return res.data
  } catch (err: any) {
    console.error(err)
    throw new Error(err?.message ?? 'Failed to invite user')
  }
}
